import { useEnvContext } from "context/EnvContext";
import { useExtractDocs } from "context/ExtractDocsContext";
import {
  APP_CAREPROVIDER,
  FILE_TABLE_ACTION_UPLOAD,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  TRACK_EVENTS,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { processDocumentFile, validateFile } from "core/model/files";
import {
  ALLOWED_FILE_MIME_TYPES,
  MAX_UPLOAD_FILES,
} from "core/model/files/config";
import {
  AuctionRequest,
  FileHISStateful,
  FileState,
  Filev2,
  QueryProgress,
} from "core/types";
import { Modal } from "ds/ui";
import { Button, type NonIconOnlyButtonProps } from "ds/ui/button/button";
import {
  type GeneralListAction,
  type SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import {
  useFilesFeatures,
  type FileFeatureData,
} from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { useIsEligiblePage } from "dsl/ecosystems/PatientFiles/useUploadFile";
import { getFileSource } from "dsl/ecosystems/PatientFiles/utils";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import { UploadIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTracking } from "react-tracking";
import { useLoggedCareseekerConfig } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { DragNDropScreen } from "./Screens/DragNDropScreen";
import {
  FileUploadScreen,
  type FileUploadInitialValues,
  type UploadedFileResult,
} from "./Screens/FileUploadScreen";
import { HISFileUploadScreen } from "./Screens/HISFileUploadScreen";
import type { SavedUploadFormDraft } from "./savedUploadFormDraft";

export type ProcessedFile = {
  content: string;
  file: File;
};

export type FileUploadSubmitHandler = (
  data: UploadedFileResult,
  options?: {
    onStarted?: () => void;
    onComplete?: (file: Filev2) => Promise<void>;
  },
) => Promise<Filev2 | null>;

function getUploadFileDedupeKey(file: File): string {
  return `${file.name}\0${file.size}\0${file.lastModified}`;
}

function getProcessedFilesStableKey(files: ProcessedFile[]): string {
  if (!files.length) {
    return "";
  }
  return `${files.length}:${[
    ...files.map((p) => getUploadFileDedupeKey(p.file)),
  ]
    .sort((a, b) => a.localeCompare(b))
    .join(",")}`;
}

export function useFileUpload() {
  const { trackEvent } = useTracking();
  const toast = useToast();
  const isEligiblePage = useIsEligiblePage();
  const translations = useTranslations();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const processedFileSeenKeysRef = useRef<Set<string>>(new Set());
  const [processedFiles, setProcessedFiles] = useState<ProcessedFile[]>([]);
  const [fileUploadProgress, setFileUploadProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const processFile = useCallback(
    async (file: File | undefined) => {
      try {
        if (!file) {
          throw new Error(translations.fileUploader.fileUploadError);
        }

        const dedupeKey = getUploadFileDedupeKey(file);
        if (processedFileSeenKeysRef.current.has(dedupeKey)) {
          return;
        }
        processedFileSeenKeysRef.current.add(dedupeKey);

        await validateFile({ file, isEligiblePage, trackEvent, translations });

        const content = await processDocumentFile(file);
        setProcessedFiles((prev) => {
          if (prev.some((p) => getUploadFileDedupeKey(p.file) === dedupeKey)) {
            return prev;
          }
          return [...prev, { content, file }];
        });
      } catch (error) {
        if (file) {
          processedFileSeenKeysRef.current.delete(getUploadFileDedupeKey(file));
        }
        toast({ color: "danger", message: (error as Error).message });
      }
    },
    [isEligiblePage, toast, trackEvent, translations],
  );

  const handleChange = useCallback(
    async (files: FileList | null) => {
      try {
        if (!files?.length) {
          throw new Error(
            translations.careproviderApp.settings.uploadFiles.errorMaxFiles({
              maxCount: String(MAX_UPLOAD_FILES),
            }),
          );
        }
        setFileUploadProgress(QUERY_PROGRESS_PENDING);
        await Promise.all(Array.from(files).map((file) => processFile(file)));
        setFileUploadProgress(QUERY_PROGRESS_SUCCEED);
      } catch (error) {
        setFileUploadProgress(QUERY_PROGRESS_FAILED);
        toast({ color: "danger", message: (error as Error).message });
      }
    },
    [processFile, toast, translations],
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setProcessedFiles((prev) => {
      const removed = prev[index];
      if (removed) {
        processedFileSeenKeysRef.current.delete(
          getUploadFileDedupeKey(removed.file),
        );
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const reset = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setProcessedFiles([]);
    processedFileSeenKeysRef.current.clear();
  }, []);

  return {
    fileUploadProgress,
    handleChange,
    handleClick,
    handleRemoveFile,
    inputRef,
    processedFiles,
    reset,
  };
}

export function UploadFilesModal({
  action,
  allowedFileTypes,
  availableFiles,
  getFilesHISProgress,
  handleSubmit,
  handleUploadFilesHIS,
  isDisabled = true,
  isMessenger,
  mutationProgress,
  reset,
  setAction,
  unavailableFiles,
  uploadButtonProps,
}: {
  action: GeneralListAction<Filev2> | undefined;
  allowedFileTypes?: readonly string[] | string[];
  auctionRequest?: AuctionRequest;
  availableFiles: FileState[] | null;
  getFilesHISProgress: QueryProgress;
  handleSubmit: FileUploadSubmitHandler;
  handleUploadFilesHIS: ReturnType<
    typeof useFilesFeatures
  >["handleUploadFilesHIS"];
  isDisabled?: boolean;
  isMessenger?: boolean;
  mutationProgress: QueryProgress;
  reset: () => void;
  setAction: SetAction<FileFeatureData>;
  unavailableFiles: FileHISStateful[] | null;
  uploadButtonProps?: Omit<NonIconOnlyButtonProps, "ref" | "onPress">;
}) {
  const { app } = useEnvContext();
  const { isTablet } = useMedia();
  const translations = useTranslations();
  const toast = useToast();
  const { trackEvent } = useTracking();
  const hasActiveOnPrem = useLoggedCareseekerConfig(
    "on_premise_archive_integration_enabled",
  );
  const isEligiblePage = useIsEligiblePage();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const { extractOcr } = useExtractDocs();

  const {
    fileUploadProgress,
    handleChange,
    handleClick,
    handleRemoveFile,
    inputRef,
    processedFiles,
    reset: resetStagingFromHook,
  } = useFileUpload();

  const [uploadScreenMountKey, setUploadScreenMountKey] = useState(0);
  const resetFile = useCallback(() => {
    resetStagingFromHook();
    setUploadScreenMountKey((k) => k + 1);
  }, [resetStagingFromHook]);

  const processedFilesStableKey = useMemo(
    () => getProcessedFilesStableKey(processedFiles),
    [processedFiles],
  );

  const prevProcessedCountRef = useRef(0);
  const wasModalOpenRef = useRef(false);
  const savedFormDataRef = useRef<SavedUploadFormDraft>({
    categories: [],
    fileNames: [],
  });

  const isModalOpen = !!action?.type;
  const showExtractCheckbox =
    (isDocsAndExtractActive || isDischargeLetterActive) && !isEligiblePage;

  const uploadTableKisDocumentId = action?.data?.kis_document_id_string;

  const fileUploadScreenInitialValues =
    useMemo((): FileUploadInitialValues[] => {
      if (action?.type !== FILE_TABLE_ACTION_UPLOAD) {
        return [];
      }
      return processedFiles.map((processedFile) => ({
        category: null,
        content: processedFile.content,
        file: processedFile.file,
        fileName: processedFile.file.name,
        kis_document_id_string: uploadTableKisDocumentId,
        should_extract_on_upload: showExtractCheckbox,
      }));
    }, [
      action?.type,
      processedFiles,
      showExtractCheckbox,
      uploadTableKisDocumentId,
    ]);

  async function submit(fileUploadData: UploadedFileResult[]) {
    if (!fileUploadData.length) return;

    savedFormDataRef.current = { categories: [], fileNames: [] };
    resetFile();
    reset();

    trackEvent({
      count: String(fileUploadData.length),
      cta: "upload",
      modal: FILE_TABLE_ACTION_UPLOAD,
      name: TRACK_EVENTS.FILES_UPLOADED,
      should_extract_on_upload: fileUploadData.some((fileData) =>
        Boolean(fileData.should_extract_on_upload),
      ),
      sources: fileUploadData
        .map((fileData) => getFileSource(fileData))
        .join(","),
    });

    await Promise.all(
      fileUploadData.map(async (fileData, i) => {
        const processedFile = processedFiles[i];

        if (!processedFile) {
          throw new Error("Processed file missing for upload");
        }

        trackEvent({
          cta: "upload",
          file_implementation: "HIS",
          modal: FILE_TABLE_ACTION_UPLOAD,
          name: TRACK_EVENTS.FILE_MODAL_CTA_CLICKED,
          should_extract_on_upload: Boolean(fileData.should_extract_on_upload),
          source: getFileSource(fileData),
        });

        await handleSubmit(fileData, {
          onComplete: async (file) => {
            if (fileData.should_extract_on_upload && fileData.file) {
              try {
                await extractOcr.handleOcrForFile({
                  file: fileData.file,
                  fileCategory: fileData.category,
                  id: file.id,
                });
              } catch {
                toast({ color: "danger", message: translations.general.error });
              }
            }
          },
        });
      }),
    );
  }

  const close = useCallback(() => {
    savedFormDataRef.current = { categories: [], fileNames: [] };
    resetFile();
    reset();
  }, [reset, resetFile]);

  const handleSelectFileV2 = useCallback(() => {
    if (app === APP_CAREPROVIDER) {
      if (isTablet) {
        handleClick();
      } else {
        setAction({
          data: {} as FileFeatureData,
          type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
        });
      }
    } else {
      setAction({
        data: {} as FileFeatureData,
        type: hasActiveOnPrem
          ? FILE_TABLE_ACTION_UPLOAD_HIS
          : FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
      });

      trackEvent({
        file_implementation: "HIS",
        name: TRACK_EVENTS.SELECT_FILE_CTA_CLICKED,
      });
    }
  }, [app, isTablet, hasActiveOnPrem, trackEvent, handleClick, setAction]);

  useEffect(() => {
    const len = processedFiles.length;
    const prevLen = prevProcessedCountRef.current;

    if (len === 0) {
      prevProcessedCountRef.current = 0;
      return;
    }

    if (prevLen > 0) {
      prevProcessedCountRef.current = len;
      return;
    }

    prevProcessedCountRef.current = len;
    if (!processedFilesStableKey) {
      return;
    }
    setUploadScreenMountKey((k) => k + 1);
    setAction({
      data: {} as FileFeatureData,
      type: FILE_TABLE_ACTION_UPLOAD,
    });
  }, [processedFiles, processedFilesStableKey, setAction]);

  useEffect(() => {
    wasModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  const renderModalContent = () => {
    switch (action?.type) {
      case FILE_TABLE_ACTION_UPLOAD_HIS:
        return (
          <HISFileUploadScreen
            availableFiles={availableFiles}
            getFilesHISProgress={getFilesHISProgress}
            handleUploadFilesHIS={handleUploadFilesHIS}
            isMessenger={isMessenger}
            mutationProgress={mutationProgress}
            setAction={setAction}
            unavailableFiles={unavailableFiles}
          />
        );
      case FILE_TABLE_ACTION_UPLOAD_DRAGNDROP:
        return (
          <DragNDropScreen
            handleChange={handleChange}
            processedFiles={processedFiles}
            setAction={setAction}
          />
        );
      case FILE_TABLE_ACTION_UPLOAD:
        if (!processedFiles.length) {
          return null;
        }
        return (
          <FileUploadScreen
            key={String(uploadScreenMountKey)}
            fileUploadProgress={fileUploadProgress}
            handleRemoveFile={handleRemoveFile}
            initialValues={fileUploadScreenInitialValues}
            mutationProgress={mutationProgress}
            savedFormDataRef={savedFormDataRef}
            setAction={setAction}
            onSubmit={submit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <Button
          id="upload_file"
          startContent={<UploadIcon />}
          tour={tourAttributes({
            stepKey: PRODUCT_TOURS.files_page.steps.select_files.key,
            tourKey: PRODUCT_TOURS.files_page.key,
          })}
          {...uploadButtonProps}
          className="z-0 m-0"
          color="primary"
          isDisabled={isDisabled}
          size="sm"
          variant="ghost"
          onPress={handleSelectFileV2}
        >
          {uploadButtonProps?.children ??
            translations.fileSection.fileUploadComponent.uploadFile}
        </Button>
        <div className="flex flex-col">
          <input
            ref={inputRef}
            accept={(allowedFileTypes ?? ALLOWED_FILE_MIME_TYPES).join(",")}
            className="hidden"
            data-testid="file-input"
            disabled={isDisabled}
            name="name"
            type="file"
            onChange={(event) => handleChange(event.target.files)}
          />
        </div>
      </div>

      <Modal
        disableAnimation={wasModalOpenRef.current && isModalOpen}
        isDismissable={false}
        isOpen={isModalOpen}
        placement="center"
        size="3xl"
        onClose={() => {
          trackEvent({
            cta: "outside_modal",
            file_implementation: "HIS",
            modal: action?.type,
            name: TRACK_EVENTS.UPLOAD_MODAL_CLOSED,
          });
          close();
        }}
      >
        {renderModalContent()}
      </Modal>
    </>
  );
}
